import classNames from 'classnames';
import { useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { useTranslation } from 'react-i18next';
import ButtonSwitchMulti from '../../components/actions/ButtonSwitchMulti';
import HelpNavigate from '../../helpContext/HelpNavigate';
import { MetricGroupConfig } from '../store/actuality.types';
import { MetricGroupId } from '../store/metricGroups.config';

interface MetricGroupSettingsProps {
  groups: Record<MetricGroupId, MetricGroupConfig>;
  onToggleVisibility: (groupId: MetricGroupId) => void;
  onUpdateOrder: (newOrder: Record<MetricGroupId, number>) => void;
}

const MetricGroupSettings = ({
  groups,
  onToggleVisibility,
  onUpdateOrder,
}: MetricGroupSettingsProps) => {
  const { t } = useTranslation('feed');
  const [mode, setMode] = useState<'visibility' | 'order'>('visibility');

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const items = Array.from(Object.entries(groups));
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    const newOrder = items.reduce(
      (acc, [id], index) => {
        acc[id as MetricGroupId] = index + 1;
        return acc;
      },
      {} as Record<MetricGroupId, number>,
    );

    onUpdateOrder(newOrder);
  };

  const groupEntries = Object.entries(groups).sort(
    ([, a], [, b]) => (a.order || 1000) - (b.order || 1000),
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">
          {t('Stats (Heatmap Metrics) Settings')}
        </h3>
        <ButtonSwitchMulti
          autoSize
          options={[{ label: t('Visibility') }, { label: t('Sort Order') }]}
          selectedIndex={mode === 'visibility' ? 0 : 1}
          buttonType="helper"
          onClick={() =>
            setMode(mode === 'visibility' ? 'order' : 'visibility')
          }
        />
      </div>

      {mode === 'visibility' ? (
        <ul className="space-y-4">
          {groupEntries.map(([id, group]) => (
            <li className={classNames('flex items-start')} key={id}>
              <div className="flex flex-1 items-center">
                {group.helpNavTo && (
                  <HelpNavigate
                    className="!mx-0.5 !mr-2"
                    href={group.helpNavTo}
                  />
                )}
                <label
                  className={classNames(
                    'flex flex-1 truncate',
                    !group.visible ? 'opacity-50' : 'opacity-80',
                  )}
                >
                  {group.title}
                </label>
              </div>
              <ButtonSwitchMulti
                autoSize
                options={[{ label: t('off') }, { label: t('on') }]}
                selectedIndex={group.visible ? 1 : 0}
                buttonType={group.visible ? 'secondary' : 'minimal'}
                onClick={() => onToggleVisibility(id as MetricGroupId)}
              />
            </li>
          ))}
        </ul>
      ) : (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="metric-groups">
            {provided => (
              <ul
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="flex flex-col gap-1"
              >
                {groupEntries.map(([id, group], index) => (
                  <Draggable key={id} draggableId={id} index={index}>
                    {provided => (
                      <li
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={classNames(
                          'bg-menu flex items-center rounded-md p-2',
                          !group.visible && 'opacity-50',
                        )}
                      >
                        <span className="mr-2 text-gray-500">{index + 1}.</span>
                        <span>{group.title}</span>
                      </li>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </ul>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </div>
  );
};

export default MetricGroupSettings;
